/* Reviews — App Store now, others later. App switcher + platform pills (top-level,
   de-nested) · custom stat cards · rating breakdown · AI-clustered themes as full
   rows · filterable feed with reply-with-Claude. App Store Connect is an [I DO]
   (coming soon); the UI is complete. */
import { useState } from 'react';
import { Button } from '../../ds';
import { WidgetBody } from '../../ds/mc-kit';
import { useContract } from '../../lib/api';
import { useOps } from '../ops';
import { Panel, Kicker } from '../ui';
import { integrationFor } from '../../lib/flags';
import { deriveReviews } from '../../lib/selectors';

export function Reviews() {
  const reviews = useContract('reviews', { pollMs: 60_000 });
  const apps = useContract('apps', { pollMs: 0 });
  const { pushToast, appendAudit } = useOps();
  const [filter, setFilter] = useState('all');
  const [repliedIds, setRepliedIds] = useState<string[]>([]);
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const connect = integrationFor('reviews')?.connect;

  const R = reviews.data;
  const d = R ? deriveReviews(R, filter, repliedIds, apps.data ?? []) : null;
  const app = d?.app ?? apps.data?.[0];

  const openReply = (id: string) => { setReplyingId(id); setDraft(''); };
  const draftReply = (id: string, text: string) => { setReplyingId(id); setDraft(text); };
  const sendReply = (id: string) => {
    setRepliedIds((r) => [...r, id]);
    setReplyingId(null);
    appendAudit({ actor: 'Cassidy', action: 'Replied to review', target: `TrailQuest · ${id}` });
    pushToast('success', 'Reply published', 'Your response is live on the App Store.');
  };
  const themeToTask = (label: string, issue?: string) => {
    appendAudit({ actor: 'Cassidy', action: 'Briefed the crew', target: label + (issue ? ` — ${issue}` : '') });
    pushToast('info', 'Briefed the crew', label);
  };

  return (
    <div data-screen-label="Reviews" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>

      {/* === APP SWITCHER + PLATFORM PILLS === */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <button onClick={() => {/* app switch */}} aria-label="Switch app"
          style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '7px 12px 7px 7px', cursor: 'pointer' }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', fontSize: 18, background: 'var(--surface2)', border: '1px solid var(--border)', flex: 'none' }}>
            {app?.glyph ?? '📱'}
          </span>
          <span style={{ textAlign: 'left' }}>
            <span style={{ display: 'block', fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{app?.name ?? 'TrailQuest'}</span>
            <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>{app?.tag ?? 'iOS · App Store'}</span>
          </span>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2 }} aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {(d?.platforms ?? []).map((p) => (
            p.connected ? (
              <span key={p.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 999, background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)', whiteSpace: 'nowrap' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 7px var(--accent)', animation: 'ct-pulse 2s ease-in-out infinite' }} />
                {p.name} <span style={{ color: 'var(--muted)' }}>· {p.note}</span>
              </span>
            ) : (
              <span key={p.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 11px', borderRadius: 999, background: 'transparent', border: '1px dashed var(--border)', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                {p.name}
                <span style={{ padding: '1px 6px', borderRadius: 999, background: 'var(--surface2)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase' }}>soon</span>
              </span>
            )
          ))}
        </div>

        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 8px var(--accent)', animation: 'ct-pulse 2s ease-in-out infinite' }} />
          live
        </span>
      </div>

      {/* === CUSTOM STAT CARDS (4 col) === */}
      <div className="mc-auto mc-auto-md">
        {/* Rating card */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Overall rating</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>{R?.ratingDelta ?? '—'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 30, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{R?.rating ?? '—'}</span>
            <span style={{ fontSize: 14, letterSpacing: 1, color: 'var(--warn)' }}>★★★★★</span>
          </div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{R?.totalRatings ?? '—'} ratings · App Store</div>
        </div>
        {/* Needs reply card (warn border) */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--warn-line)', borderRadius: 'var(--radius-lg)', padding: '15px 17px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Needs your reply</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--warn)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warn)' }} />
              queue
            </span>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 30, letterSpacing: '-0.02em', color: 'var(--warn)' }}>{R?.needsReply ?? '—'}</div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>awaiting a response</div>
        </div>
        {/* New this week card */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>New this week</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>+34</span>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 30, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{R?.weekVolume ?? '—'}</div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>reviews · last 7 days</div>
        </div>
        {/* 1★ this week card */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>1★ this week</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--danger)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--danger)' }} />
              watch
            </span>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 30, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{R?.oneStarWeek ?? '—'}</div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>critical · triage first</div>
        </div>
      </div>

      {/* === BREAKDOWN + THEMES (0.85fr 1.15fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '0.85fr 1.15fr' } as React.CSSProperties}>
        {/* Rating breakdown */}
        <Panel title="// rating breakdown" label="Rating breakdown">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {(d?.dist ?? []).map((row) => (
              <div key={row.stars} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 10 }}>
                <span style={{ width: 30, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink)', flex: 'none' }}>{row.stars}<span style={{ color: 'var(--warn)' }}>★</span></span>
                <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${row.pct}%`, borderRadius: 999, background: row.barColor }} />
                </div>
                <span style={{ width: 92, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', flex: 'none' }}>{row.n} · {row.pct}%</span>
              </div>
            ))}
          </div>
        </Panel>

        {/* Themes — full rows, titled "// what people are saying" */}
        <Panel
          label="What people are saying"
          title={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)' }}>// what people are saying</span>}
          aside={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--primary)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.4 2.4M15.3 15.3l2.4 2.4M17.7 6.3l-2.4 2.4M8.7 15.3l-2.4 2.4" /></svg>
              clustered by Claude
            </span>
          }>
          <WidgetBody state={reviews.state} minHeight={180} rows={4}
            emptyLabel="No themes yet" errorLabel="Clustering unavailable" onRetry={reviews.refetch}>
            <div>
              {(d?.themes ?? []).map((t) => (
                <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: t.soft, border: `1px solid ${t.line}`, color: t.inkColor, fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap', flex: 'none' }}>{t.kind}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', flex: 'none' }}>{t.n}</span>
                  {t.isIssue && (
                    <button onClick={() => themeToTask(t.label, t.issue)}
                      style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 'var(--radius-sm)', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      → crew
                    </button>
                  )}
                </div>
              ))}
            </div>
          </WidgetBody>
        </Panel>
      </div>

      {/* === REVIEWS FEED === */}
      <section aria-label="Reviews feed" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <Kicker>// reviews</Kicker>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {(d?.filters ?? []).map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'var(--font-heading)', fontWeight: 500, fontSize: 12.5, background: f.chipBg, border: `1px solid ${f.chipBorder}`, color: f.chipText }}>
                {f.label} <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>{f.count}</span>
              </button>
            ))}
          </div>
        </div>

        <WidgetBody state={reviews.state} minHeight={240} rows={4}
          emptyLabel="No reviews yet" errorLabel="App Store unavailable" errorSub="Couldn't reach App Store Connect." onRetry={reviews.refetch}>
          {d?.empty && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '30px 12px', textAlign: 'center' }}>
              <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>Nothing in this filter</span>
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Try another filter above.</span>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(d?.feed ?? []).map((r) => (
              <div key={r.id} style={{ background: 'var(--bg)', border: `1px solid ${r.critical ? 'var(--danger-line)' : 'var(--border)'}`, borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, letterSpacing: 1, whiteSpace: 'nowrap' }}>
                        <span style={{ color: 'var(--warn)' }}>{r.filledStars}</span>
                        <span style={{ color: 'var(--surface2)' }}>{r.emptyStars}</span>
                      </span>
                      <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{r.title}</span>
                      {r.critical && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, background: 'var(--danger-soft)', border: '1px solid var(--danger-line)', color: 'var(--danger)', fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--danger)' }} />
                          critical
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>{r.body}</div>
                    <div style={{ marginTop: 9, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{r.flag} {r.author} · v{r.ver} · {r.when} ago</div>
                  </div>
                  <div style={{ flex: 'none' }}>
                    {r.replied ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 999, background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', color: 'var(--accent-ink)', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                        replied
                      </span>
                    ) : replyingId !== r.id ? (
                      <button onClick={() => draftReply(r.id, R?.drafts[r.id] ?? 'Thanks so much for the review — it genuinely helps. Reach us at cassidy@carrtech.ai anytime.')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--font-heading)', fontWeight: 500, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.4 2.4M15.3 15.3l2.4 2.4M17.7 6.3l-2.4 2.4M8.7 15.3l-2.4 2.4" /></svg>
                        Draft reply
                      </button>
                    ) : null}
                  </div>
                </div>
                {replyingId === r.id && (
                  <div style={{ marginTop: 13, borderTop: '1px solid var(--border)', paddingTop: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.4 2.4M15.3 15.3l2.4 2.4M17.7 6.3l-2.4 2.4M8.7 15.3l-2.4 2.4" /></svg>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.04em', color: 'var(--accent-ink)' }}>// draft with Claude — edit, then publish</span>
                    </div>
                    <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} aria-label="Reply draft"
                      style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', padding: '11px 13px', borderRadius: 'var(--radius-md)', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--ink)', fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 1.55, outline: 'none' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                      <button onClick={() => openReply(r.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.4 2.4M15.3 15.3l2.4 2.4M17.7 6.3l-2.4 2.4M8.7 15.3l-2.4 2.4" /></svg>
                        Regenerate
                      </button>
                      <span style={{ flex: 1 }} />
                      <Button variant="ghost" size="sm" onClick={() => setReplyingId(null)}>Cancel</Button>
                      <Button size="sm" onClick={() => sendReply(r.id)}>Publish reply</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </WidgetBody>

        {connect && reviews.isPlaceholder && (
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>
            {connect} · showing sample data
          </div>
        )}
      </section>
    </div>
  );
}
